import { FleetAutoBookingSkipReason } from '@navio/nsdk';
import type { NoddiIconName } from 'noddi-ui-common';
import { businessTime } from '../date/businessTime';
import { daysFromToday } from './signalRowText';

/** What a manager does about a blocked fleet car: add bookable time, fix the schedule, or nothing. */
export type FleetAutoBookingFixGroup = 'capacity' | 'configuration' | 'cancelled';

interface FleetAutoBookingReasonCopy {
  group: FleetAutoBookingFixGroup;
  /** What happened, in one sentence. */
  explainer: string;
}

export const FLEET_AUTO_BOOKING_REASON_COPY: Record<FleetAutoBookingSkipReason, FleetAutoBookingReasonCopy> = {
  [FleetAutoBookingSkipReason.INSUFFICIENT_CAPACITY]: {
    group: 'capacity',
    explainer: 'Every slot that fits the schedule was full when the run tried to book these cars.'
  },
  [FleetAutoBookingSkipReason.AREA_CLOSED_FOR_NEW_BOOKINGS]: {
    group: 'capacity',
    explainer: 'The service area is closed for new bookings on this date.'
  },
  [FleetAutoBookingSkipReason.SLOT_TOO_SHORT]: {
    group: 'capacity',
    explainer: 'The free time on this date is shorter than the service takes for these cars.'
  },
  [FleetAutoBookingSkipReason.NO_AVAILABILITY_SLOT_ON_DATE]: {
    group: 'configuration',
    explainer: 'None of the schedule’s availability slots fall on this date.'
  },
  [FleetAutoBookingSkipReason.SCHEDULE_MISSING_AVAILABILITY]: {
    group: 'configuration',
    explainer: 'The schedule has no availability slots, so the run can’t pick a time.'
  },
  [FleetAutoBookingSkipReason.SCHEDULE_MISSING_ADDRESS]: {
    group: 'configuration',
    explainer: 'The schedule has no address, so the run can’t place the booking.'
  },
  [FleetAutoBookingSkipReason.SCHEDULE_MISSING_SALES_ITEMS]: {
    group: 'configuration',
    explainer: 'The schedule has no services, so there is nothing to book.'
  },
  [FleetAutoBookingSkipReason.SCHEDULE_MISSING_DEPARTMENT_CONTACT]: {
    group: 'configuration',
    explainer: 'The schedule has no department contact, which bookings need.'
  },
  [FleetAutoBookingSkipReason.NO_SERVICE_DEPARTMENT_AREA]: {
    group: 'configuration',
    explainer: 'The schedule’s address isn’t inside any of the department’s service areas.'
  },
  [FleetAutoBookingSkipReason.PREVIOUSLY_CANCELLED_ON_TARGET_DATE]: {
    group: 'cancelled',
    explainer: 'Someone cancelled the booking on this date, so the run left it alone.'
  }
};

interface FleetAutoBookingFixCopy {
  icon: NoddiIconName;
  howToFix: string;
}

export const FLEET_AUTO_BOOKING_FIX_COPY: Record<FleetAutoBookingFixGroup, FleetAutoBookingFixCopy> = {
  capacity: {
    icon: 'Users',
    howToFix: 'Add bookable time on this date, or book the cars manually.'
  },
  configuration: {
    icon: 'Settings2',
    howToFix: 'Fix the fleet schedule’s configuration in Noddi Admin.'
  },
  cancelled: {
    icon: 'Undo2',
    howToFix: 'Nothing is wrong. Book another date if the cars still need the service.'
  }
};

// The backend's daily fleet auto-booking run, in Oslo time.
const AUTO_BOOKING_RUN_HOUR = 5;
const AUTO_BOOKING_RUN_MINUTE = 30;

export const AUTO_BOOKING_RUN_TIME = '05:30';
// The run books from the day after it runs (the backend's AUTO_BOOKING_MIN_LEAD_DAYS).
const AUTO_BOOKING_MIN_LEAD_DAYS = 1;

const isBeforeTodaysRun = (now: Date): boolean => {
  const oslo = businessTime(now);
  return oslo.getHours() * 60 + oslo.getMinutes() < AUTO_BOOKING_RUN_HOUR * 60 + AUTO_BOOKING_RUN_MINUTE;
};

/** "today at 05:30" before the run, "tomorrow at 05:30" after it, read on the Oslo clock. */
export const nextAutoBookingRun = (now: Date): string =>
  `${isBeforeTodaysRun(now) ? 'today' : 'tomorrow'} at ${AUTO_BOOKING_RUN_TIME}`;

/** The run whose result a fleet signal shows: "today 05:30", or "yesterday 05:30" before today's has run. */
export const lastAutoBookingRun = (now: Date): string =>
  `${isBeforeTodaysRun(now) ? 'yesterday' : 'today'} ${AUTO_BOOKING_RUN_TIME}`;

/** Whether the next auto-booking run still books `date` (YYYY-MM-DD): a day it runs on, or before, is too close. */
export const nextAutoBookingRunCovers = (date: string, now: Date): boolean =>
  daysFromToday(date, now) >= (isBeforeTodaysRun(now) ? 0 : 1) + AUTO_BOOKING_MIN_LEAD_DAYS;

/** What happens to blocked cars once bookable time is added on their date. */
export const fleetAddedShiftCopy = (date: string, now: Date): string =>
  nextAutoBookingRunCovers(date, now)
    ? `The next auto-booking run, ${nextAutoBookingRun(now)}, tries to book the cars again.`
    : 'The day is too close for the next auto-booking run, so book the cars manually.';

/** Why a fleet signal has no resolve action: what makes it go away on its own. */
export const fleetAutoBookingClearsCopy = (group: FleetAutoBookingFixGroup, now: Date): string =>
  group === 'cancelled'
    ? 'This signal goes away once the cars have an open booking on the schedule, or after the date passes.'
    : `A car drops off as soon as it has an open booking on the schedule. Other fixes show after the next auto-booking run, ${nextAutoBookingRun(now)}.`;
